// ✅ CartContext.jsx (multi-client + reset après logout)
import React, { createContext, useState, useContext, useEffect } from "react";
import clientConfig from "../../client/config/clientConfig";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  // 🗝️ stockage du panier par client (clé unique)
  const clientKey = localStorage.getItem("selectedClient") || clientConfig?.key || "default";
  const storageKey = `cartItems_${clientKey}`;

  const [cartItems, setCartItems] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  // 🧠 Persistance du panier
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(cartItems));
  }, [cartItems, storageKey]);

  // ➕ Ajouter un produit
  const addToCart = (item) => {
    setCartItems((prev) => {
      const currentQty = prev[item._id]?.quantity || 0;
      return {
        ...prev,
        [item._id]: { item, quantity: currentQty + 1 },
      };
    });
    setIsCartOpen(true); // ouvre la sidebar
  };

  // ➖ Diminuer la quantité
  const decreaseQuantity = (itemId) => {
    setCartItems((prev) => {
      if (!prev[itemId]) return prev;
      const currentQty = prev[itemId].quantity;
      if (currentQty <= 1) {
        const newCart = { ...prev };
        delete newCart[itemId];
        return newCart;
      } else {
        return {
          ...prev,
          [itemId]: {
            ...prev[itemId],
            quantity: currentQty - 1,
          },
        };
      }
    });
  };

  // ❌ Supprimer un produit
  const removeFromCart = (itemId) => {
    setCartItems((prev) => {
      const newCart = { ...prev };
      delete newCart[itemId];
      return newCart;
    });
  };

  // 🧼 Vider complètement le panier (ex: après commande ou logout)
  const clearCart = () => {
    setCartItems({});
    localStorage.removeItem(storageKey);
  };

  // 📊 Calcul total
  const totalQuantity = Object.values(cartItems).reduce(
    (sum, { quantity }) => sum + quantity,
    0
  );
  const getTotalPrice = () =>
    Object.values(cartItems).reduce(
      (sum, { item, quantity }) => sum + item.price * quantity,
      0
    );

  // 🔁 Contrôle ouverture sidebar
  const toggleCart = () => setIsCartOpen((open) => !open);
  const closeCart = () => setIsCartOpen(false);
  const openCart = () => setIsCartOpen(true);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        decreaseQuantity,
        removeFromCart,
        clearCart,
        totalQuantity,
        getTotalPrice,
        isCartOpen,
        toggleCart,
        closeCart,
        openCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
